import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../redux/store";
import CloseIcon from "../../../../../assets/svg/close-icon";
import CircularArrowBackIcon from "../../../../../assets/svg/circular-arrow-back-icon";
import PlusIcon from "../../../../../assets/svg/plus-icon";
import { FilterDropdown } from "../../HelpDesk/components/CreateView/components/FilterConditionRow";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip";
import Button from "../../../../../components/Button/Button";
import ChevronIcon from "../../../../../assets/svg/chevron-icon";
import { DealsFilterGroupBlock } from "./DealsFilterGroupBlock";
import {
  type FilterGroupUI,
  createEmptyGroup,
  DEALS_VIEW_FILTER_FIELD_OPTIONS,
  DEALS_DISABLED_FIELDS,
  getDealsOperatorsForField,
} from "../utils/dealsViewConstants";
import type { FilterFieldOption } from "../../HelpDesk/components/CreateView/utils/constants";
import { getDealCurrencyIconByRegion } from "../utils/functions";

interface DealsCreateViewFiltersProps {
  filterGroups: FilterGroupUI[];
  logicalOperator: "and" | "or";
  onFilterGroupsChange: (groups: FilterGroupUI[]) => void;
  onLogicalOperatorChange: (op: "and" | "or") => void;
  onBack: () => void;
  onClose: () => void;
  onSave: () => void;
  isEditMode?: boolean;
  isSaveViewLoading?: boolean;
  hasUnsavedChanges?: boolean;
}

const AddFilterDropdown = ({
  onSelect,
  fieldOptions,
}: {
  onSelect: (fieldKey: string) => void;
  fieldOptions: FilterFieldOption[];
}) => {
  return (
    <FilterDropdown
      options={fieldOptions.map((f) => ({
        key: f.key,
        label: f.label,
      }))}
      onChange={(key) => {
        if (DEALS_DISABLED_FIELDS[key]) return;
        onSelect(key);
      }}
      width="w-auto"
      dropdownClassName="!min-w-[200px] !max-w-[220px] !max-h-[400px] !left-0 !top-[55px]"
      customTrigger={() => (
        <div className="bg-verba-indigo dark:bg-verba-indigo-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk rounded-full w-[60px] h-[48px] flex items-center justify-center shadow-card border-[2.5px] border-border-gray dark:border-border-gray-dk hover:scale-105 transition-transform">
          <PlusIcon
            className="w-5 h-5 text-pure-white dark:text-pure-white-dk"
            strokeWidth={1.1}
            stroke="currentColor"
          />
        </div>
      )}
      renderOption={(opt) => {
        const field = fieldOptions.find((f) => f.key === opt.key);
        if (!field) return opt.label;
        const Icon = field.icon;
        const tooltipText = DEALS_DISABLED_FIELDS[opt.key];
        const content = (
          <div
            className={`flex items-center gap-1.5 w-full ${tooltipText ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <Icon className="shrink-0 w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
            <TruncatedTextTooltip text={field.label} maxWidth="max-w-[140px]" />
          </div>
        );
        if (tooltipText) {
          return (
            <Tooltip content={tooltipText} side="right">
              {content}
            </Tooltip>
          );
        }
        return content;
      }}
    />
  );
};

const TopLevelLogicalToggle = ({
  value,
  onChange,
  disabled = false,
}: {
  value: "and" | "or";
  onChange: (val: "and" | "or") => void;
  disabled?: boolean;
}) => {
  if (disabled) {
    return (
      <span className="px-2 py-1 text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk uppercase select-none">
        {value?.toUpperCase()}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onChange(value === "and" ? "or" : "and")}
      className="flex items-center gap-0.5 px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded-md text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk cursor-pointer transition-colors uppercase"
    >
      {value?.toUpperCase()}
      <ChevronIcon className="w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk" />
    </button>
  );
};

export const DealsCreateViewFilters = ({
  filterGroups,
  logicalOperator,
  onFilterGroupsChange,
  onLogicalOperatorChange,
  onBack,
  onClose,
  onSave,
  isEditMode = false,
  isSaveViewLoading = false,
  hasUnsavedChanges = true,
}: DealsCreateViewFiltersProps) => {
  const clientRegion = useSelector(
    (state: RootState) => state.auth?.client?.region
  );
  const RegionCurrencyIcon = getDealCurrencyIconByRegion(clientRegion);
  const fieldOptions = useMemo(() => {
    return DEALS_VIEW_FILTER_FIELD_OPTIONS.map((f) =>
      f.key === "total_amount" ? { ...f, icon: RegionCurrencyIcon } : f
    );
  }, [RegionCurrencyIcon]);

  const hasFilters = filterGroups?.length > 0;

  const hasValidFilters = useMemo(
    () =>
      filterGroups?.some((g) =>
        g?.conditions?.some((c) => c?.key && c?.value?.length > 0)
      ) ?? false,
    [filterGroups]
  );

  const handleFooterCancel = () => {
    if (isEditMode) {
      onClose();
    } else {
      onBack();
    }
  };

  const handleAddFilterFromEmpty = (fieldKey: string) => {
    const newGroup = createEmptyGroup();
    newGroup.conditions[0].key = fieldKey;
    newGroup.conditions[0].comparison_operator =
      getDealsOperatorsForField(fieldKey)[0]?.key ?? "contains";
    onFilterGroupsChange([...filterGroups, newGroup]);
  };

  const handleAddFilter = () => {
    onFilterGroupsChange([...filterGroups, createEmptyGroup()]);
  };

  const handleGroupChange = (idx: number, updated: FilterGroupUI) => {
    const newGroups = [...filterGroups];
    newGroups[idx] = updated;
    onFilterGroupsChange(newGroups);
  };

  const handleDeleteGroup = (idx: number) => {
    onFilterGroupsChange(filterGroups?.filter((_, i) => i !== idx));
  };

  const handleClearAll = () => {
    onFilterGroupsChange([]);
  };

  return (
    <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk overflow-hidden rounded-l-[8px]">
      <div className="border-b border-border-gray dark:border-border-gray-dk flex items-center justify-between px-3 h-[50px] shrink-0 rounded-tl-[8px]">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer"
            aria-label="Back to view details"
          >
            <CircularArrowBackIcon className="w-5 h-5 text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
          <span className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            Filters
          </span>
        </div>
        <div className="flex items-center gap-4">
          {hasFilters && (
            <button
              onClick={handleClearAll}
              className="h-[26px] px-3 flex items-center justify-center border border-verba-red-alert dark:border-verba-red-alert-dk rounded-md text-xs font-medium text-verba-red-alert dark:text-verba-red-alert-dk hover:bg-verba-red-alert dark:hover:bg-verba-red-alert-dk hover:text-fixed-white dark:hover:text-fixed-white-dk transition-colors cursor-pointer"
            >
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="w-[18px] h-[18px] bg-tag-gray dark:bg-tag-gray-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer"
            aria-label="Close"
          >
            <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-y-auto">
        {!hasFilters ? (
          <div className="absolute top-[15%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-6 px-4">
            <AddFilterDropdown onSelect={handleAddFilterFromEmpty} fieldOptions={fieldOptions} />
            <span className="text-lg text-verba-indigo dark:text-verba-indigo-dk">
              Click to add a &quot;Filter&quot;
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-3 px-4 py-6">
            {filterGroups?.map((group, gIdx) => (
              <div
                key={gIdx}
                className={`flex ${group?.conditions?.length > 1 ? "items-center" : "items-start"}`}
              >
                {gIdx > 0 && (
                  <div
                    className={`${group?.conditions?.length > 1 ? "" : "pt-3"} w-[70px] shrink-0`}
                  >
                    <TopLevelLogicalToggle
                      value={logicalOperator}
                      onChange={onLogicalOperatorChange}
                      disabled={gIdx > 1}
                    />
                  </div>
                )}
                <DealsFilterGroupBlock
                  group={group}
                  onGroupChange={(updated) => handleGroupChange(gIdx, updated)}
                  onDeleteGroup={() => handleDeleteGroup(gIdx)}
                  isFirstGroup={gIdx === 0}
                  fieldOptions={fieldOptions}
                  filterGroups={filterGroups}
                />
              </div>
            ))}

            <div className="pl-[70px] pt-3">
              <button
                type="button"
                onClick={handleAddFilter}
                className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] px-10 flex items-center gap-1 cursor-pointer"
              >
                <PlusIcon className="w-3 h-3 text-verba-blue dark:text-verba-blue-dk" />
                <span className="text-md font-semibold text-verba-blue dark:text-verba-blue-dk">
                  Add Filter
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="px-[18px] py-[18px] backdrop-blur-[20px] shrink-0 border-t border-border-gray dark:border-border-gray-dk rounded-bl-[8px]">
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            onClick={handleFooterCancel}
            innerText="Cancel"
            disabled={isSaveViewLoading}
            buttonContainerClass="h-[26px] w-[100px] shrink-0 border border-border-gray dark:border-border-gray-dk rounded-md text-md font-semibold text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors bg-transparent cursor-pointer"
            innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
          />
          <Button
            type="button"
            onClick={onSave}
            innerText={isSaveViewLoading ? "Saving..." : "Save View"}
            disabled={
              !hasValidFilters || isSaveViewLoading || !hasUnsavedChanges
            }
            buttonContainerClass="h-[26px] px-12 shrink-0 bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md enabled:cursor-pointer disabled:!cursor-not-allowed transition-opacity hover:enabled:opacity-90 disabled:opacity-50 disabled:hover:opacity-50"
            innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </div>
    </div>
  );
};
